import { useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "../../hooks/useForm";
import { useCalibration } from "../../contexts/CalibrationContext";
import { useShedConfig } from "../../contexts/ShedConfigContext";
import Section from "../layout/Section";
import InfoTooltip from "../layout/InfoTooltip";
import { usePdfGeneration } from "../../hooks/usePdfGeneration";
import { getProfile } from "@/api/profile";
import { MATERIALS_LIMITS } from "../../utils/constants";

export default function GenerateReportSection() {
  const navigate = useNavigate();
  const form = useForm();
  const calibration = useCalibration();
  const shedConfig = useShedConfig();
  const { generatePdf, isGenerating } = usePdfGeneration();

  // Fetch profile data for builder branding in PDF
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validateBeforeDownload = useCallback(() => {
    const errors: string[] = [];

    if (!form.name?.trim()) errors.push("Name is required");
    if (!form.email?.trim()) errors.push("Email is required");
    if (!form.phone?.trim()) errors.push("Phone number is required");
    if (!form.address?.trim()) errors.push("Address is required");

    if (!calibration.isSurveyLoaded)
      errors.push("Survey image/PDF must be uploaded");
    if (!calibration.isCalibrationComplete)
      errors.push("Property size must be calibrated");
    if (!calibration.isShedLocked)
      errors.push("Shed position must be finalized and locked");

    if (!shedConfig.shedStyle) errors.push("Shed style must be selected");
    if (!shedConfig.dimensions.width) errors.push("Shed width is required");
    if (!shedConfig.dimensions.length) errors.push("Shed length is required");
    if (!shedConfig.dimensions.wallHeight)
      errors.push("Wall height is required");
    if (!shedConfig.dimensions.totalHeight)
      errors.push("Total height is required");
    if (!shedConfig.dimensions.wallExteriorColor)
      errors.push("Wall exterior color is required");
    if (!shedConfig.configApplied)
      errors.push("Shed configuration must be applied");

    if (!form.materialType) {
      errors.push("Roof material type must be selected");
    } else {
      if (form.materialType === "metal" && !form.metalColor) {
        errors.push("Metal color is required");
      }
      if (form.materialType === "shingles") {
        if (!form.shingleType) errors.push("Shingle type is required");
        if (!form.shingleColor) errors.push("Shingle color is required");
        if (!form.shingleBrand) errors.push("Shingle brand is required");
      }
    }

    if (
      form.hasWindSpeed === true &&
      (!form.windSpeed || form.windSpeed.trim() === "")
    ) {
      errors.push("Wind speed (MPH) is required when wind speed is enabled");
    }

    if (form.materialsText && form.materialsText.length > MATERIALS_LIMITS.maxChars) {
      errors.push(
        `Materials text exceeds the ${MATERIALS_LIMITS.maxChars}-character limit — please shorten it before downloading`,
      );
    }
    if (form.materialsText && form.materialsText.split("\n").length > MATERIALS_LIMITS.maxLines) {
      errors.push(
        `Materials text exceeds the ${MATERIALS_LIMITS.maxLines}-line limit — please shorten it before downloading`,
      );
    }

    return errors;
  }, [form, calibration, shedConfig]);

  const handleDownloadClick = useCallback(() => {
    const errors = validateBeforeDownload();

    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowErrorModal(true);
    } else {
      setShowReviewModal(true);
    }
  }, [validateBeforeDownload]);

  const handleReviewConfirm = useCallback(() => {
    setShowReviewModal(false);
    setShowConfirmModal(true);
  }, []);

  const handleReviewEdit = useCallback(() => {
    setShowReviewModal(false);
  }, []);

  const handleConfirmYes = useCallback(async () => {
    setShowConfirmModal(false);
    const success = await generatePdf({
      form,
      calibration,
      shedConfig,
      profile,
    });
    if (success) {
      navigate("/home");
    }
  }, [form, calibration, shedConfig, generatePdf, profile, navigate]);

  const handleConfirmNo = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  return (
    <Section title="Generate Report">
      <div className="text-sm mx-4 md:mx-40 mb-4 mt-2">
        Review everything before downloading. If you need to make changes, you
        can go back and adjust any section before generating the document. Once
        you've completed all previous steps, click the "Download PDF Report"
        button to generate your final document.
      </div>

      <button
        onClick={handleDownloadClick}
        disabled={isGenerating}
        className={`bg-[#2f5241] hover:bg-[#2f5241e1] text-white px-5 py-2 rounded mt-4 hover:cursor-pointer ${
          isGenerating ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {isGenerating ? "Generating..." : "Download PDF Report"}
      </button>

      <InfoTooltip>
        <ul>
          Confirm that you have all of the following:
          <li>The lot plan with your structure located</li>
          <li>Selected dimensions and materials</li>
          <li>Technical data such as anchoring, wind speed and roof type</li>
          <li>All necessary elements to present to your HOA or municipality</li>
        </ul>
      </InfoTooltip>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            style={{
              userSelect: "none",
              WebkitUserSelect: "none",
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="bg-[#2f5241] text-white p-6">
              <h2 className="text-2xl font-bold text-center">
                Review Your Information
              </h2>
              <p className="text-center text-sm mt-2 opacity-90">
                Please verify all information before generating the PDF
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Basic Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b-2 border-[#2f5241] pb-2">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">Name:</span>
                    <span className="ml-2 text-gray-900">
                      {form.name || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Email:</span>
                    <span className="ml-2 text-gray-900">
                      {form.email || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Phone:</span>
                    <span className="ml-2 text-gray-900">
                      {form.phone || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Address:</span>
                    <span className="ml-2 text-gray-900">
                      {form.address || "Not provided"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Shed Configuration */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b-2 border-[#2f5241] pb-2">
                  Shed Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">
                      Shed Style:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {shedConfig.shedStyle || "Not selected"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Wall Exterior Color:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {shedConfig.dimensions.wallExteriorColor ||
                        "Not selected"}
                    </span>
                  </div>
                  {shedConfig.shedStyle === "lean-to" &&
                    shedConfig.isAttached !== null && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Attached:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {shedConfig.isAttached ? "Yes" : "No"}
                        </span>
                      </div>
                    )}
                </div>
              </div>

              {/* Dimensions */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b-2 border-[#2f5241] pb-2">
                  Dimensions
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">Width:</span>
                    <span className="ml-2 text-gray-900">
                      {shedConfig.dimensions.width}'{" "}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Length:</span>
                    <span className="ml-2 text-gray-900">
                      {shedConfig.dimensions.length}'
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Wall Height:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {shedConfig.dimensions.wallHeight}'
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Total Height:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {shedConfig.dimensions.totalHeight}'
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Fence Distance:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {shedConfig.dimensions.fenceDistance}'
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Distance from Ground to Shed:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {shedConfig.dimensions.floorDistance}"
                    </span>
                  </div>
                </div>
              </div>

              {/* Technical Details */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b-2 border-[#2f5241] pb-2">
                  Technical Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">Blocks:</span>
                    <span className="ml-2 text-gray-900">
                      {shedConfig.hasBlocks === true
                        ? "Yes"
                        : shedConfig.hasBlocks === false
                          ? "No"
                          : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Anchored:</span>
                    <span className="ml-2 text-gray-900">
                      {form.isAnchored === true ? "Yes" : "No"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Wind Speed:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {form.hasWindSpeed === true
                        ? `Yes - ${form.windSpeed || 0} MPH`
                        : "No"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Roof Materials */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b-2 border-[#2f5241] pb-2">
                  Roof Materials
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">Type:</span>
                    <span className="ml-2 text-gray-900">
                      {form.materialType || "Not selected"}
                    </span>
                  </div>
                  {form.materialType?.toLowerCase() === "metal" && (
                    <div>
                      <span className="font-medium text-gray-700">
                        Metal Color:
                      </span>
                      <span className="ml-2 text-gray-900">
                        {form.metalColor || "Not specified"}
                      </span>
                    </div>
                  )}
                  {form.materialType?.toLowerCase() === "shingles" && (
                    <>
                      <div>
                        <span className="font-medium text-gray-700">
                          Shingle Type:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {form.shingleType || "Not specified"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">
                          Shingle Color:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {form.shingleColor === "Other"
                            ? form.shingleColorOther || "Not specified"
                            : form.shingleColor || "Not selected"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">
                          Shingle Brand:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {form.shingleBrand === "Other"
                            ? form.shingleBrandOther || "Not specified"
                            : form.shingleBrand || "Not selected"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Materials */}
              {form.materialsText && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b-2 border-[#2f5241] pb-2">
                    Materials
                  </h3>
                  <div className="text-gray-900 whitespace-pre-wrap">
                    {form.materialsText}
                  </div>
                </div>
              )}

              {/* Calibration Status */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b-2 border-[#2f5241] pb-2">
                  Survey & Calibration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">
                      Survey Uploaded:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {calibration.isSurveyLoaded ? "\u2713 Yes" : "\u2717 No"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Calibration Complete:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {calibration.isCalibrationComplete
                        ? "\u2713 Yes"
                        : "\u2717 No"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Shed Position Locked:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {calibration.isShedLocked ? "\u2713 Yes" : "\u2717 No"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <div className="flex">
                  <div className="shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Please review all information carefully. Once you confirm,
                      the PDF will be generated with this data.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={handleReviewEdit}
                  className="px-8 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors hover:cursor-pointer"
                >
                  Edit Information
                </button>
                <button
                  onClick={handleReviewConfirm}
                  className="px-8 py-3 text-sm font-medium text-white bg-[#2f5241] border-2 border-transparent rounded-md hover:bg-[#2f5241e1] focus:outline-none focus:ring-2 focus:ring-[#2f5241] focus:ring-offset-2 transition-colors hover:cursor-pointer"
                >
                  Everything is OK, Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 text-center mb-4">
                Missing Required Information
              </h3>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">
                  Please complete the following before generating the PDF:
                </p>
                <ul className="space-y-2 text-left">
                  {validationErrors.map((error, index) => (
                    <li
                      key={index}
                      className="flex items-start text-sm text-red-700"
                    >
                      <svg
                        className="w-4 h-4 mr-2 mt-0.5 shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 hover:cursor-pointer"
              >
                OK, I'll complete the missing information
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md mx-4 transform transition-all duration-300">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-full">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Download PDF Report
              </h3>

              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you checked that everything is ok?
              </p>

              <div className="flex justify-center space-x-4">
                <button
                  type="button"
                  onClick={handleConfirmNo}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-colors hover:cursor-pointer"
                >
                  NO
                </button>
                <button
                  type="button"
                  onClick={handleConfirmYes}
                  className="px-6 py-2 text-sm font-medium text-white bg-[#2f5241] border border-transparent rounded-md hover:bg-[#2f5241e1] focus:outline-none focus:ring-2 focus:ring-[#2f5241] focus:ring-offset-2 transition-colors hover:cursor-pointer"
                >
                  YES
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center max-w-sm mx-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2f5241] mx-auto mb-4" />
            <p className="text-gray-700 text-lg font-semibold">
              Generating PDF Report...
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Please wait while we create your document
            </p>
          </div>
        </div>
      )}
    </Section>
  );
}
